import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, AlertTriangle, CreditCard, Lock, ShieldCheck, Wrench, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { paymentsApi } from "../lib/api";
import { PoweredBy } from "../components/PoweredBy";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const money = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PayDeposit() {
  const { estimateId } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const requested = parseFloat(params.get("amount") || "0");

  const [est, setEst] = useState(null); // null=loading | obj | false=error
  const [errorMsg, setErrorMsg] = useState("");
  const [starting, setStarting] = useState(false);
  const [polling, setPolling] = useState(!!sessionId);
  const [payResult, setPayResult] = useState(null);
  const pollAttempts = useRef(0);

  const load = useCallback(async () => {
    try {
      setEst(await paymentsApi.getEstimate(estimateId));
    } catch (err) {
      setEst(false);
      setErrorMsg(err?.response?.data?.detail || "We couldn't find this estimate.");
    }
  }, [estimateId]);

  const poll = useCallback(async () => {
    const MAX = 8;
    try {
      const res = await paymentsApi.status(sessionId);
      if (res.payment_status === "paid") {
        setPayResult(res);
        setPolling(false);
        if (res.estimate) setEst((p) => ({ ...(p || {}), ...res.estimate }));
        return;
      }
      if (res.status === "expired") {
        setPayResult({ payment_status: "expired" });
        setPolling(false);
        return;
      }
    } catch (_) {
      /* keep trying */
    }
    pollAttempts.current += 1;
    if (pollAttempts.current >= MAX) {
      setPayResult({ payment_status: "timeout" });
      setPolling(false);
      return;
    }
    setTimeout(poll, 2000);
  }, [sessionId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (sessionId) {
      pollAttempts.current = 0;
      poll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const payAmount = Math.min(requested || est?.depositDue || 0, est?.depositDue || 0);

  const startPayment = async () => {
    setStarting(true);
    setErrorMsg("");
    try {
      const res = await paymentsApi.createDepositCheckout(estimateId, window.location.origin, payAmount);
      window.location.href = res.url;
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || "Could not start the deposit. Please try again.");
      setStarting(false);
    }
  };

  // When the staff payment popup opened this page in a new tab, auto-close it
  // after success — the popup back in the app updates the records live.
  useEffect(() => {
    if (payResult?.payment_status === "paid" && window.opener) {
      const t = setTimeout(() => window.close(), 3000);
      return () => clearTimeout(t);
    }
  }, [payResult]);

  const accent = est && est.company?.accentColor ? est.company.accentColor : "#10B981";
  const logoUrl =
    est && est.company?.logoDataUrl
      ? est.company.logoDataUrl.startsWith("http")
        ? est.company.logoDataUrl
        : `${BACKEND}${est.company.logoDataUrl}`
      : null;

  return (
    <div className="min-h-screen bg-[#0B1E3F] flex items-center justify-center p-4" data-testid="pay-estimate-page">
      <div className="w-full max-w-md">
        <button
          onClick={() => window.history.back()}
          data-testid="deposit-back-btn"
          className="mb-3 inline-flex items-center gap-1.5 text-[13px] font-semibold text-white/70 hover:text-white transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div className="flex items-center justify-center gap-2.5 mb-5">
          {logoUrl ? (
            <img src={logoUrl} alt="logo" className="h-9 max-w-[160px] object-contain" />
          ) : (
            <>
              <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center">
                <Wrench className="h-5 w-5 text-white" strokeWidth={2.5} />
              </div>
              <div className="text-white font-bold text-[17px] tracking-tight">{est?.company?.name || "ServicePro"}</div>
            </>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
          {est === null && (
            <div className="py-20 flex flex-col items-center justify-center text-slate-400">
              <Loader2 className="h-6 w-6 animate-spin mb-2" />
              <div className="text-[13px]">Loading…</div>
            </div>
          )}

          {est === false && (
            <div className="py-16 px-6 text-center" data-testid="deposit-error">
              <div className="h-12 w-12 rounded-full bg-rose-50 flex items-center justify-center mx-auto mb-3">
                <AlertTriangle className="h-6 w-6 text-rose-500" />
              </div>
              <div className="text-[15px] font-semibold text-slate-900">Estimate unavailable</div>
              <div className="text-[13px] text-slate-500 mt-1">{errorMsg}</div>
            </div>
          )}

          {est && est !== false && (
            <>
              {polling && (
                <div className="py-20 flex flex-col items-center justify-center text-slate-500" data-testid="deposit-polling">
                  <Loader2 className="h-7 w-7 animate-spin mb-3" style={{ color: accent }} />
                  <div className="text-[14px] font-semibold text-slate-800">Confirming your deposit…</div>
                </div>
              )}

              {!polling && payResult?.payment_status === "paid" && (
                <div className="py-12 px-6 text-center" data-testid="deposit-success">
                  <div className="h-14 w-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3">
                    <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                  </div>
                  <div className="text-[18px] font-bold text-slate-900">Payment received</div>
                  <div className="text-[13px] text-slate-500 mt-1">
                    Thank you! Your payment toward estimate <strong>{est.id}</strong> was successful.
                  </div>
                  <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4 text-left">
                    <Row label="Estimate total" value={money(payResult.estimate?.total ?? est.total)} />
                    <Row label="Paid" value={money(payResult.estimate?.depositPaid ?? est.depositPaid)} strong />
                    <Row label="Remaining" value={money(payResult.estimate?.depositDue ?? est.depositDue)} />
                  </div>
                  {window.opener && (
                    <div className="mt-4 text-[12px] text-slate-400" data-testid="deposit-success-close-hint">
                      This tab will close automatically — the records are already updated.
                    </div>
                  )}
                </div>
              )}

              {!polling && payResult && payResult.payment_status !== "paid" && (
                <div className="py-12 px-6 text-center" data-testid="deposit-failed">
                  <div className="h-12 w-12 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-3">
                    <AlertTriangle className="h-6 w-6 text-amber-500" />
                  </div>
                  <div className="text-[16px] font-bold text-slate-900">
                    {payResult.payment_status === "expired" ? "Session expired" : "Still processing"}
                  </div>
                  <Button
                    onClick={() => {
                      setPayResult(null);
                      window.location.href = `/pay/deposit/${estimateId}?amount=${requested}`;
                    }}
                    className="mt-4 h-10 text-white text-[13px] font-semibold"
                    style={{ background: accent }}
                    data-testid="deposit-retry"
                  >
                    Try again
                  </Button>
                </div>
              )}

              {!polling && !payResult && (
                <>
                  <div className="px-6 pt-6 pb-4 border-b border-slate-100">
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-400">
                      Estimate {est.id}
                    </div>
                    <div className="text-[15px] font-semibold text-slate-900 mt-0.5">{est.customer}</div>
                    {est.depositDue <= 0 ? (
                      <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-50 text-emerald-700 text-[12.5px] font-semibold border border-emerald-200">
                        <CheckCircle2 className="h-4 w-4" /> Fully paid — nothing due
                      </div>
                    ) : (
                      <div className="mt-3 flex items-end justify-between">
                        <span className="text-[12.5px] text-slate-500">Amount due</span>
                        <span className="text-[28px] font-bold text-slate-900 leading-none">{money(payAmount)}</span>
                      </div>
                    )}
                  </div>

                  <div className="px-6 py-3 border-b border-slate-100 text-[12.5px] text-slate-500 flex items-center justify-between">
                    <span>Estimate total {money(est.total)}</span>
                    <span>Already paid {money(est.depositPaid)}</span>
                  </div>

                  {est.depositDue > 0 && est.onlinePaymentEnabled && (
                    <div className="px-6 py-5 space-y-3">
                      {errorMsg && (
                        <div data-testid="deposit-form-error" className="px-3 py-2 rounded-md bg-rose-50 border border-rose-200 text-[12.5px] text-rose-700">
                          {errorMsg}
                        </div>
                      )}
                      <Button
                        onClick={startPayment}
                        disabled={starting || payAmount < 0.5}
                        data-testid="deposit-pay-btn"
                        className="w-full h-12 text-white text-[14px] font-bold rounded-lg"
                        style={{ background: accent }}
                      >
                        {starting ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirecting to checkout…</>
                        ) : (
                          <><CreditCard className="h-4 w-4 mr-2" /> Pay {money(payAmount)}</>
                        )}
                      </Button>
                      <div className="flex items-center justify-center gap-1.5 text-[11px] text-slate-400 pt-1">
                        <Lock className="h-3 w-3" /> Secure payment powered by Stripe
                      </div>
                    </div>
                  )}

                  {est.depositDue > 0 && !est.onlinePaymentEnabled && (
                    <div className="px-6 py-5" data-testid="deposit-online-unavailable">
                      <div className="rounded-lg border border-amber-200 bg-amber-50/80 px-4 py-3.5 text-[12.5px] text-amber-800 flex items-start gap-2">
                        <Lock className="h-4 w-4 shrink-0 mt-0.5" />
                        <span>Online payments aren't set up for this business yet. Please contact them to pay your deposit.</span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-center gap-1.5 text-[11px] text-slate-500 mt-4">
          <ShieldCheck className="h-3.5 w-3.5" /> Your card details are processed securely by Stripe.
        </div>
        <PoweredBy dark className="mt-3" />
      </div>
    </div>
  );
}

function Row({ label, value, strong }) {
  return (
    <div className="flex items-center justify-between py-0.5">
      <span className="text-[12.5px] text-slate-500">{label}</span>
      <span className={`text-[13px] ${strong ? "font-bold text-slate-900" : "font-medium text-slate-700"}`}>{value}</span>
    </div>
  );
}
